import React, { type MouseEventHandler, useEffect, useState } from 'react';

import TemplateThumbnailPreview from '@dashboard/components/TemplateThumbnailPreview';
import { type EmailTemplateHtmlPreviewType } from '@dashboard/generated/wp/platform';
import plusIconSrc from '@dashboard/images/waypoint-plus.png';
import { Box, Card, CardActionArea, CardContent, CardMedia, Stack, Typography } from '@mui/material';

type Props = {
  title: string;
  active?: boolean;
  actions?: JsxNode;
  disabled?: boolean;
  onClick: (sampleTemplateId: string | null) => void;
  templateType: EmailTemplateHtmlPreviewType;
  templateId: string | null;
};

const MEDIA_PROPS = {
  overflow: 'hidden',
  height: 196,
  backgroundPosition: 'top',
  borderColor: 'cadet.200',
};
const BLANK_TEMPLATE_MEDIA_PROPS = {
  ...MEDIA_PROPS,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export default function SampleTemplateCard({
  title,
  actions,
  active,
  disabled,
  templateId,
  templateType,
  onClick,
}: Props) {
  const [ref, setRef] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (active && ref) {
      ref.scrollIntoView();
      ref.parentElement?.parentElement?.scrollBy(0, -16);
    }
  }, [ref, active]);

  const renderPreview = () => {
    if (templateId === null) {
      return <Box component="img" src={plusIconSrc} alt="Plus icon" sx={{ maxWidth: 48, mt: 1 }} />;
    }
    return <TemplateThumbnailPreview id={templateId} type={templateType} />;
  };
  const clickHandler: MouseEventHandler<HTMLButtonElement> = (ev) => {
    ev.preventDefault();
    onClick(templateId);
  };

  return (
    <Card
      ref={setRef}
      variant="outlined"
      sx={{
        borderColor: active ? 'brand.green' : undefined,
        borderWidth: active ? '2px' : '1px',
        gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4', lg: 'span 3' },
      }}
    >
      <CardActionArea disabled={disabled} onClick={clickHandler}>
        <CardMedia sx={templateId === null ? BLANK_TEMPLATE_MEDIA_PROPS : MEDIA_PROPS}>{renderPreview()}</CardMedia>
        <CardContent sx={{ borderTop: '1px solid', borderColor: 'divider', py: 1.5, px: 2 }}>
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
            <Typography fontWeight="medium" noWrap>
              {title}
            </Typography>
            {actions}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
